/**
 * The send state of a CR, as the server says it (`get_meeting_report_send_state`). Pure:
 * parsing and French wording only — the rules themselves live in SQL.
 */

export type MeetingReportSendReason =
  | 'not_found'
  | 'calendar_meeting'
  | 'not_generated'
  | 'already_sent'
  | 'declined'
  | 'appointment_not_held'
  | 'sending'
  | 'send_unknown';

export interface MeetingReportSendState {
  sendable: boolean;
  /** Null when sendable. An unknown code (newer SQL) is kept as is and shown generically. */
  reason: string | null;
  subject: string;
  generatedAt: string | null;
  recipient: string | null;
  recipientName: string | null;
  /** The meeting's external participants: the copies every surface offers, the send's default. */
  defaultCc: string[];
}

const text = (row: Record<string, unknown>, key: string): string | null =>
  typeof row[key] === 'string' && (row[key] as string).trim() ? (row[key] as string) : null;

export function parseMeetingReportSendState(data: unknown): MeetingReportSendState | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const row = data as Record<string, unknown>;
  return {
    sendable: row.sendable === true,
    reason: text(row, 'reason'),
    subject: text(row, 'subject') ?? 'Compte-rendu',
    generatedAt: text(row, 'cr_generated_at'),
    recipient: text(row, 'recipient'),
    recipientName: text(row, 'recipient_name'),
    defaultCc: Array.isArray(row.default_cc)
      ? row.default_cc.filter((email): email is string => typeof email === 'string' && email.trim() !== '')
      : [],
  };
}

const EMAIL_SHAPE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * The copies typed in a field (commas, semicolons or line breaks): the addresses, and what is not
 * one. Pure. Duplicates and the recipient are the server's to drop (`_shared/cr-email.ts`).
 */
export function parseCopies(text: string): { emails: string[]; invalid: string[] } {
  const parts = text.split(/[,;\n]+/).map((part) => part.trim()).filter(Boolean);
  return {
    emails: parts.filter((part) => EMAIL_SHAPE.test(part)),
    invalid: parts.filter((part) => !EMAIL_SHAPE.test(part)),
  };
}

/** The field's text for a list of copies. Pure. */
export function formatCopies(emails: readonly string[]): string {
  return emails.join(', ');
}

/** Why the copies cannot leave, or null. Pure. */
export function copiesError(text: string): string | null {
  const { invalid } = parseCopies(text);
  return invalid.length > 0 ? `Adresse en copie invalide : ${invalid.join(', ')}` : null;
}

const SEND_REASON_TEXT: Record<MeetingReportSendReason, string> = {
  not_found: 'Compte rendu introuvable.',
  calendar_meeting: "Notes d'une réunion de l'agenda : elles ne s'envoient pas au client.",
  not_generated: "Le compte rendu n'est pas encore rédigé.",
  already_sent: 'Compte rendu déjà envoyé.',
  declined: 'Compte rendu gardé sans envoi (« Ne pas envoyer ») : il ne part plus.',
  appointment_not_held: "Rendez-vous annulé ou absent : le compte rendu ne s'envoie pas.",
  sending: 'Envoi en cours.',
  send_unknown:
    "Un envoi a commencé il y a plus de 15 minutes sans se conclure. Vérifie dans tes Envoyés Gmail qu'il n'est pas parti avant de le débloquer.",
};

export function sendReasonText(reason: string | null): string | null {
  if (!reason) return null;
  return (SEND_REASON_TEXT as Record<string, string>)[reason] ?? `Envoi impossible (${reason}).`;
}
